import { TanStackPostalCodeField } from "@sellar-io/components/adapters";
import { Button } from "@sellar-io/components";
import { CountryCodeDictionary, CountryType } from "@sellar-io/common";
import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { SpinnerLegacy } from "../../../../components";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import { DeliveryAddressType } from "../OrderOnBehalfOf.shared";

export const EditDeliveryAddressDialog = ({
  active,
  onDismiss,
  address: initAddress,
  onSubmit,
  loading,
  description,
  title,
}: {
  active: boolean;
  onDismiss: () => void;
  address?: DeliveryAddressType;
  onSubmit: (data: DeliveryAddressType) => void;
  loading?: boolean;
  description?: string;
  title?: string;
}) => {
  const form = useForm({
    defaultValues: initAddress ?? {
      name: "",
      street: "",
      city: "",
      region: "",
      postalCode: "",
      country: "GB",
    },
    onSubmit: ({ value }) => {
      onSubmit(value as DeliveryAddressType);
    },
  });

  useEffect(() => {
    if (initAddress) {
      form.setFieldValue("name", initAddress.name);
      form.setFieldValue("street", initAddress.street);
      form.setFieldValue("city", initAddress.city);
      form.setFieldValue("region", initAddress.region);
      form.setFieldValue("postalCode", initAddress.postalCode);
      form.setFieldValue("country", initAddress.country as CountryType);
    }
  }, [initAddress, form]);

  const isFormValid = useStore(form.store, (state) => state.isValid);

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={() => onDismiss()}
        title={title ?? "Change delivery address"}
        description={description}
      />
      <DialogContent>
        {loading ? (
          <SpinnerLegacy />
        ) : (
          <form
            className={"flex flex-col gap-2"}
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="name"
              validators={{
                onChange: ({ value }) =>
                  !value.trim() ? "Address line 1 is required" : undefined,
                onBlur: ({ value }) =>
                  !value.trim() ? "Address line 1 is required" : undefined,
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <InputContainer
                  className={
                    !state.meta.isValid && state.meta.errors.length > 0
                      ? "border-red-300 focus-within:border-red-500"
                      : undefined
                  }
                >
                  <InputText
                    defaultValue={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    placeholder={"Address line 1"}
                  />
                </InputContainer>
              )}
            </form.Field>
            <form.Field name="street">
              {({ state, handleChange, handleBlur }) => (
                <InputContainer>
                  <InputText
                    defaultValue={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    placeholder={"Address line 2 (optional)"}
                  />
                </InputContainer>
              )}
            </form.Field>
            <form.Field
              name="city"
              validators={{
                onChange: ({ value }) =>
                  !value.trim() ? "City is required" : undefined,
                onBlur: ({ value }) =>
                  !value.trim() ? "City is required" : undefined,
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <InputContainer
                  className={
                    !state.meta.isValid && state.meta.errors.length > 0
                      ? "border-red-300 focus-within:border-red-500"
                      : undefined
                  }
                >
                  <InputText
                    defaultValue={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    placeholder={"City"}
                  />
                </InputContainer>
              )}
            </form.Field>
            <form.Field
              name="region"
              validators={{
                onChange: ({ value }) =>
                  !value.trim() ? "Region is required" : undefined,
                onBlur: ({ value }) =>
                  !value.trim() ? "Region is required" : undefined,
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <InputContainer
                  className={
                    !state.meta.isValid && state.meta.errors.length > 0
                      ? "border-red-300 focus-within:border-red-500"
                      : undefined
                  }
                >
                  <InputText
                    defaultValue={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    placeholder={"Region"}
                  />
                </InputContainer>
              )}
            </form.Field>
            <TanStackPostalCodeField
              form={form}
              name="postalCode"
              countryField="country"
              validationMode="changeAndBlur"
            />
            <form.Field
              name="country"
              validators={{
                onChange: ({ value }) =>
                  !value.trim() ? "Country is required" : undefined,
                onBlur: ({ value }) =>
                  !value.trim() ? "Country is required" : undefined,
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <InputContainer
                  className={
                    !state.meta.isValid && state.meta.errors.length > 0
                      ? "border-red-300 focus-within:border-red-500"
                      : undefined
                  }
                >
                  <InputSelect
                    defaultValue={state.value}
                    onChange={(e) =>
                      handleChange(e.target.value as CountryType)
                    }
                    onBlur={handleBlur}
                    placeholder={"Country"}
                  >
                    {Object.keys(CountryCodeDictionary).map((key) => (
                      <InputOption key={key} value={key}>
                        {
                          CountryCodeDictionary[
                            key as keyof typeof CountryCodeDictionary
                          ]
                        }
                      </InputOption>
                    ))}
                  </InputSelect>
                </InputContainer>
              )}
            </form.Field>
            <Button
              type="submit"
              className={"w-full"}
              disabled={!isFormValid || loading}
            >
              Update
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
